import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormSelect,
  CFormSwitch,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilBan, cilCheckCircle, cilInfo, cilLayers, cilReload } from '@coreui/icons'

import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import ToastComponent from 'src/components/common/TaostComponent'
import FixtureService from 'src/service/FixtureService'
import { STAT_GROUPS } from '../ScoreCard/scorecardConfig'
import './FixtureSetting.css'

// The API keeps a column list per scorecard surface: 1 = the Manage Scores entry
// grid, 2 = the read-only scorecard on completed fixtures. The screen used to
// show a switch for each, which let a stat be typed in on one screen and go
// missing on the other. One switch per stat now writes both lists.
const SCORECARD_TYPES = [1, 2]

const GROUP_META = {
  batting: { label: 'Batting' },
  bowling: { label: 'Bowling' },
  fielding: { label: 'Fielding' },
  other: { label: 'Other' },
}

// Same banding as the entry grid (`scorecardConfig.STAT_GROUPS`), so a stat sits
// under the same discipline here as it does on the scorecard it controls.
// Anything no band claims falls into "Other" rather than disappearing.
const GROUP_BY_KEY = STAT_GROUPS.reduce((acc, group) => {
  group.keys.forEach((key) => {
    acc[key] = group.key
  })
  return acc
}, {})

const SEARCH_FIELDS = [
  { key: 'full_name', label: 'Stat Name' },
  { key: 'label', label: 'Stat Code' },
]

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'enabled', label: 'On the scorecard' },
  { value: 'disabled', label: 'Hidden' },
]

const COLUMNS = [
  { key: 'full_name', label: 'Stat', filter: false, _style: { width: '42%' } },
  { key: 'group', label: 'Discipline', filter: false, _style: { width: '20%' } },
  { key: 'enabled', label: 'Status', filter: false, _style: { width: '20%' } },
  {
    key: 'toggle',
    label: 'Show On Scorecard',
    filter: false,
    sorter: false,
    _style: { width: '18%' },
  },
]

// The borderless-alert InfoBox the refreshed screens use, copied rather than
// shared - same house convention as MiniStepper/StatTile.
const InfoBox = ({ children, color = 'info' }) => (
  <CAlert color={color} className="d-flex align-items-start gap-2 border-0 mb-3">
    <CIcon icon={cilInfo} className="flex-shrink-0 mt-1" />
    <div>{children}</div>
  </CAlert>
)

const FixtureSetting = () => {
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState({})
  const [groupFilter, setGroupFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchField, setSearchField] = useState(SEARCH_FIELDS[0].key)
  const [search, setSearch] = useState('')

  // Both lists are still read on mount: type 2 is seeded server-side by its
  // first read, and `updateAttributeState` can only flip a row that exists.
  const fetchAttributes = useCallback(() => {
    setIsLoading(true)
    setLoadFailed(false)
    Promise.all([FixtureService.getFixtureAttributes(1), FixtureService.getFixtureAttributes(2)])
      .then(([manageRes, completedRes]) => {
        if (manageRes?.status !== 200 || completedRes?.status !== 200) {
          setLoadFailed(true)
          return
        }
        setRows(
          (manageRes.data || []).map((attribute) => {
            const group = GROUP_BY_KEY[attribute.attribute_key] || 'other'
            return {
              attribute_key: attribute.attribute_key,
              label: attribute.label,
              full_name: attribute.full_name,
              group,
              groupLabel: GROUP_META[group].label,
              enabled: Number(attribute.is_enabled) === 1,
            }
          }),
        )
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    fetchAttributes()
  }, [fetchAttributes])

  const setRowEnabled = (attributeKey, enabled) =>
    setRows((prev) =>
      prev.map((row) => (row.attribute_key === attributeKey ? { ...row, enabled } : row)),
    )

  const handleToggle = (row, enabled) => {
    setSaving((prev) => ({ ...prev, [row.attribute_key]: true }))
    setRowEnabled(row.attribute_key, enabled)

    Promise.all(
      SCORECARD_TYPES.map((type) =>
        FixtureService.updateAttributeState({
          attribute_key: row.attribute_key,
          is_enabled: enabled ? 1 : 0,
          type,
        }),
      ),
    )
      .then(() => {
        ToastComponent(
          `${row.full_name} is now ${enabled ? 'shown on' : 'hidden from'} the scorecards.`,
          'success',
        )
      })
      .catch(() => {
        // Both calls just set a flag, so switching the stat again re-sends them
        // and repairs a change that only landed on one of the two lists.
        setRowEnabled(row.attribute_key, !enabled)
        ToastComponent(`Could not update ${row.full_name}. Please try again.`, 'error')
      })
      .finally(() =>
        setSaving((prev) => {
          const next = { ...prev }
          delete next[row.attribute_key]
          return next
        }),
      )
  }

  const enabledCount = rows.filter((row) => row.enabled).length

  const STAT_CARDS = [
    { label: 'Stat Columns', value: rows.length, icon: cilLayers, color: 'primary' },
    { label: 'On The Scorecard', value: enabledCount, icon: cilCheckCircle, color: 'success' },
    { label: 'Hidden', value: rows.length - enabledCount, icon: cilBan, color: 'secondary' },
  ]

  // Discipline chips double as the filter, so the count of stats in each band is
  // visible before anything is clicked. A band this game has no stats for drops.
  const groupTabs = useMemo(
    () =>
      [
        { key: 'all', label: 'All Stats', count: rows.length },
        ...Object.keys(GROUP_META).map((key) => ({
          key,
          label: GROUP_META[key].label,
          count: rows.filter((row) => row.group === key).length,
        })),
      ].filter((tab) => tab.key === 'all' || tab.count > 0),
    [rows],
  )

  // Filtering is client-side: the API hands over the whole column list in one
  // read, so searching it does not need another round trip.
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase()
    const matchesSearch = (row) =>
      !term ||
      String(row[searchField] ?? '')
        .toLowerCase()
        .includes(term)
    const matchesStatus = (row) =>
      statusFilter === 'all' || (statusFilter === 'enabled' ? row.enabled : !row.enabled)
    const matchesGroup = (row) => groupFilter === 'all' || row.group === groupFilter

    return rows
      .filter((row) => matchesGroup(row) && matchesStatus(row) && matchesSearch(row))
      .map((row) => (row.enabled ? row : { ...row, _props: { className: 'fs-row-off' } }))
  }, [rows, groupFilter, statusFilter, search, searchField])

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="mb-1">Scorecard Columns</h4>
          <div className="text-medium-emphasis">
            Choose which stats your scorers enter and your players see on every fixture.
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <CButton
            color="secondary"
            variant="outline"
            onClick={fetchAttributes}
            disabled={isLoading}
          >
            <CIcon icon={cilReload} className="me-1" />
            Refresh
          </CButton>
        </div>
      </div>

      <CRow className="g-3 mb-3">
        {STAT_CARDS.map((card) => (
          <CCol sm={6} lg={4} key={card.label}>
            <CCard className="h-100">
              <CCardBody className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-medium-emphasis small">{card.label}</div>
                  <div className="fs-4 fw-semibold">{card.value}</div>
                </div>
                <div className={`fs-stat-icon fs-stat-icon--${card.color}`}>
                  <CIcon icon={card.icon} size="lg" />
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        ))}
      </CRow>

      <CCard>
        <CCardBody>
          <InfoBox>
            A stat you switch off loses its column on both the Manage Scores entry grid and the
            scorecard shown on completed fixtures. Scores already saved against it are kept - the
            column is only hidden - and switching it back on brings them straight back.
          </InfoBox>

          {loadFailed ? (
            <CAlert
              color="danger"
              className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-0"
            >
              <div>
                <strong>Could not load the scorecard columns.</strong> Check your connection and try
                again.
              </div>
              <CButton color="danger" variant="outline" size="sm" onClick={fetchAttributes}>
                <CIcon icon={cilReload} className="me-1" />
                Retry
              </CButton>
            </CAlert>
          ) : (
            <>
              <div className="fs-filters">
                <div
                  className="ge-segmented-tabs fs-group-tabs"
                  role="tablist"
                  aria-label="Filter stats by discipline"
                >
                  {groupTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={groupFilter === tab.key}
                      className={`nav-link${groupFilter === tab.key ? ' active' : ''}`}
                      onClick={() => setGroupFilter(tab.key)}
                    >
                      {tab.label}
                      <span className="fs-tab-count">{tab.count}</span>
                    </button>
                  ))}
                </div>
              </div>

              <TableSearchToolbar
                fields={SEARCH_FIELDS}
                field={searchField}
                onFieldChange={(field) => {
                  setSearchField(field)
                  setSearch('')
                }}
                value={search}
                onSearch={setSearch}
                placeholder={`Search by ${activeSearchLabel}...`}
                trailing={
                  <CFormSelect
                    className="fs-status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    aria-label="Filter stats by status"
                  >
                    {STATUS_FILTERS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </CFormSelect>
                }
              />

              <div className="ge-datatable ge-datatable--no-select">
                <CSmartTable
                  columns={COLUMNS}
                  columnSorter
                  items={filteredRows}
                  // The whole list is loaded at once and it is short, so every
                  // stat stays in one scroll instead of behind a pager.
                  itemsPerPage={Math.max(filteredRows.length, 1)}
                  loading={isLoading}
                  noItemsLabel={
                    rows.length
                      ? 'No stats match your search.'
                      : 'No scorecard columns found for this game.'
                  }
                  tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                  scopedColumns={{
                    full_name: (item) => (
                      <td>
                        <div className="fs-stat">
                          <span className="fs-code">{item.label}</span>
                          <span className="fs-stat-name">{item.full_name}</span>
                        </div>
                      </td>
                    ),
                    group: (item) => (
                      <td>
                        <span className={`fs-group-badge fs-group-badge--${item.group}`}>
                          <span className="fs-group-dot" />
                          {item.groupLabel}
                        </span>
                      </td>
                    ),
                    enabled: (item) => (
                      <td>
                        <CBadge color={item.enabled ? 'success' : 'secondary'} shape="rounded-pill">
                          {item.enabled ? 'On scorecard' : 'Hidden'}
                        </CBadge>
                      </td>
                    ),
                    toggle: (item) => (
                      <td>
                        <CFormSwitch
                          className="fs-switch"
                          id={`fs-switch-${item.attribute_key}`}
                          checked={item.enabled}
                          disabled={!!saving[item.attribute_key]}
                          aria-label={`Show ${item.full_name} on the scorecards`}
                          onChange={(e) => handleToggle(item, e.target.checked)}
                        />
                      </td>
                    ),
                  }}
                />
              </div>
            </>
          )}
        </CCardBody>
      </CCard>
    </>
  )
}

export default FixtureSetting
